import React, {
  FocusEventHandler,
  FormEventHandler,
  Fragment,
  useState,
} from 'react';
import Radio from '@mos-cat/radio';
import { Heading } from '@mos-cat/typography';
import { t, Trans } from '@lingui/macro';
import { Dispatch } from 'redux';
import { connect } from 'dva';
import { createSelector } from 'reselect';

import { MARRIAGE_INFO_FIELD_NAME } from '../../../constants/fieldNames';
import { ApplicantFields } from '../../../models/applicant';
import {
  getOnBlur,
  getPayload,
  setErrorToRelativeFields,
  trimOnBlur,
} from '../../../helpers/handlers';
import TextField from '../../shared/TextField/TextField';
import DeleteMarriageInfoPopover from '../../children/ConfirmationPopovers/DeleteMarriageInfoPopover';
import AddMarriageInfoPopover from '../../children/ConfirmationPopovers/AddMarriageInfoPopover';
import { MARITAL_STATUS } from '../../../constants/applicant';
import { getApplicant } from '../../../selectors/applicant';
import {
  getSpouseBirthDateError,
  shouldShowMarriageConfirmationPopover,
} from '../../../selectors/spouse';
import { PersonFields } from '../../../models/person';
import { ChildFields } from '../../../models/child';
import { i18n } from '../../../helpers/i18n';
import { getMarriagePlaceholder } from '../../../helpers/getPlacelholder';
import {
  maxAcceptableDate,
  minAcceptableDate,
} from '../../../helpers/dateHelpers';
import { ERROR_DATE } from '../../../constants/errors';
import { getErrorLocation } from '../../../helpers/errorLocation';
import DateField from '../../shared/DateField/DateField';
import './MarriageInfo.pcss';
import TextFieldHelper from '../../shared/TextFieldHelper/TextFieldHelper';
import { APPLICANT } from '../../../constants/modelNames';

interface StateProps {
  dateOfMarriage: ApplicantFields['dateOfMarriage'];
  marriageRecordHolder: ApplicantFields['marriageRecordHolder'];
  marriageRecordNumber: ApplicantFields['marriageRecordNumber'];
  married: ApplicantFields['married'];
  isVisible: boolean;
  shouldShowPopover: boolean;
  modalOpened: boolean;
}

interface DispatchProps {
  onChange: FormEventHandler;
  onBlur: FocusEventHandler;
  trimOnBlur: FocusEventHandler;

  maritalStatusToggle(married): { value?: string };
}

type MarriageInfoProps = StateProps & DispatchProps;

function MarriageInfo({
  isVisible,
  dateOfMarriage,
  marriageRecordHolder,
  marriageRecordNumber,
  married,
  onBlur,
  trimOnBlur,
  maritalStatusToggle,
  onChange,
  shouldShowPopover,
  modalOpened,
}: MarriageInfoProps): JSX.Element | null {
  const [
    deleteMarriageInfoConfirmVisible,
    setDeleteMarriageInfoConfirmVisible,
  ] = useState(false);
  const [
    addMarriageInfoConfirmVisible,
    setAddMarriageInfoConfirmVisible,
  ] = useState(false);

  if (!isVisible) return null;

  const isNotMarried = married.value === MARITAL_STATUS.notMarried;

  const handlePopoverAddMarriageInfoConfirm = () => {
    const payload = { married: { value: MARITAL_STATUS.married } };
    maritalStatusToggle(payload);
    setAddMarriageInfoConfirmVisible(false);
  };

  const handleChangeMarriedField = (event) => {
    if (shouldShowPopover) {
      return setAddMarriageInfoConfirmVisible(true);
    }
    const payload = getPayload(event);
    maritalStatusToggle(payload);
  };

  const isMarried = married.value === MARITAL_STATUS.married;

  const handlePopoverDeleteMarriageInfoConfirm = () => {
    const payload = { married: { value: MARITAL_STATUS.notMarried } };
    maritalStatusToggle(payload);
    setDeleteMarriageInfoConfirmVisible(false);
  };

  const handleChangeNotMarriedField = (event) => {
    if (shouldShowPopover) {
      return setDeleteMarriageInfoConfirmVisible(true);
    }
    const payload = getPayload(event);
    maritalStatusToggle(payload);
  };

  return (
    <Fragment>
      <div
        className="StepContentBlock MarriageInfo"
        onBlur={() => {
          setAddMarriageInfoConfirmVisible(false);
          setDeleteMarriageInfoConfirmVisible(false);
        }}
      >
        <Heading level={3} className="PguFormGrid__row MarriageInfo__Title">
          <Trans>Сведения о браке</Trans>
        </Heading>
        <div className="PguFormGrid__row">
          <AddMarriageInfoPopover
            isPopoverOpen={!modalOpened && addMarriageInfoConfirmVisible}
            onPopoverClose={() => setAddMarriageInfoConfirmVisible(false)}
            onConfirm={handlePopoverAddMarriageInfoConfirm}
            onCancel={() => setAddMarriageInfoConfirmVisible(false)}
          >
            <Radio
              className="PguFormGrid_col"
              label={i18n._(t`Я состою в браке`)}
              name={MARRIAGE_INFO_FIELD_NAME.married}
              value={MARITAL_STATUS.married}
              checked={isMarried}
              onChange={
                shouldShowPopover
                  ? handleChangeMarriedField
                  : handlePopoverAddMarriageInfoConfirm
              }
              error={married.error}
            />
          </AddMarriageInfoPopover>
        </div>
        <div className="PguFormGrid__row">
          <DeleteMarriageInfoPopover
            isPopoverOpen={!modalOpened && deleteMarriageInfoConfirmVisible}
            onPopoverClose={() => setDeleteMarriageInfoConfirmVisible(false)}
            onConfirm={handlePopoverDeleteMarriageInfoConfirm}
            onCancel={() => setDeleteMarriageInfoConfirmVisible(false)}
            position="bottom"
          >
            <Radio
              label={i18n._(t`Я не состою в браке`)}
              name={MARRIAGE_INFO_FIELD_NAME.married}
              value={MARITAL_STATUS.notMarried}
              checked={isNotMarried}
              onChange={
                shouldShowPopover
                  ? handleChangeNotMarriedField
                  : handlePopoverDeleteMarriageInfoConfirm
              }
              error={married.error}
            />
          </DeleteMarriageInfoPopover>
        </div>
      </div>
      {married.value === MARITAL_STATUS.married && (
        <div className="StepContentBlock">
          <div className="PguFormGrid__row">
            <DateField
              className="PguFormGrid_col"
              name={MARRIAGE_INFO_FIELD_NAME.dateOfMarriage}
              placeholder={getMarriagePlaceholder('date')}
              {...dateOfMarriage}
              onChange={onChange}
              onBlur={onBlur}
              minDate={minAcceptableDate}
              maxDate={maxAcceptableDate}
              errorLocation={getErrorLocation(
                dateOfMarriage,
                ERROR_DATE.BIRTHDATE_EARLY_MARRIAGE_DATE
              )}
            />
            <div className="PguFormGrid_col">
              <div className="TextFieldHelperWrapper">
                <TextField
                  inputProps={{
                    name: MARRIAGE_INFO_FIELD_NAME.marriageRecordNumber,
                    onChange,
                    onBlur: trimOnBlur,
                    maxLength: 200,
                    ...marriageRecordNumber,
                  }}
                  placeholder={getMarriagePlaceholder('number')}
                  icon={<div style={{ width: 30 }} />} // for correct margin to TextFieldHelper icon
                />
                <TextFieldHelper>
                  <div>
                    Номер записи акта содержит только цифры, указан
                    в&nbsp;свидетельстве о&nbsp;заключении брака, но
                    не&nbsp;совпадает с&nbsp;номером свидетельства
                  </div>
                </TextFieldHelper>
              </div>
            </div>
          </div>
          <div className="PguFormGrid__row">
            <TextField
              className="PguFormGrid_col"
              inputProps={{
                name: MARRIAGE_INFO_FIELD_NAME.marriageRecordHolder,
                onChange,
                onBlur: trimOnBlur,
                maxLength: 500,
                ...marriageRecordHolder,
              }}
              placeholder={getMarriagePlaceholder('issuedByWhom')}
            />
          </div>
        </div>
      )}
    </Fragment>
  );
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onChange(event) {
    const payload = getPayload(event);
    dispatch({
      type: `${APPLICANT}/onChange`,
      payload,
    });

    if (event.target.name === MARRIAGE_INFO_FIELD_NAME.dateOfMarriage) {
      setErrorToRelativeFields(dispatch, 'spouse', ['birthdate']);
    }
  },
  maritalStatusToggle(payload) {
    dispatch({ type: `applicant/maritalStatus/toggle`, payload });
  },
  onBlur: getOnBlur(APPLICANT, dispatch),
  trimOnBlur: trimOnBlur(APPLICANT, dispatch),
});

const mapStateToProps: (state: {
  applicant: ApplicantFields;
  children: ChildFields[];
  spouse: PersonFields;
}) => StateProps = createSelector(
  getApplicant,
  shouldShowMarriageConfirmationPopover,
  getSpouseBirthDateError,
  (state) => Object.values(state.modals).some(Boolean),
  (
    {
      address,
      dateOfMarriage,
      marriageRecordHolder,
      marriageRecordNumber,
      married,
    },
    shouldShowPopover,
    birthdateError,
    modalOpened
  ) => ({
    isVisible: address.isAddressCompleted,
    dateOfMarriage: {
      ...dateOfMarriage,
      error:
        dateOfMarriage.error ||
        (!!dateOfMarriage.value &&
          birthdateError === ERROR_DATE.BIRTHDATE_EARLY_MARRIAGE_DATE),
    },
    marriageRecordHolder,
    marriageRecordNumber,
    married,
    shouldShowPopover,
    modalOpened,
  })
);

export default connect(mapStateToProps, mapDispatchToProps)(MarriageInfo);
